import React, { useEffect, useState } from "react";
import {
  Stack,
  Button,
  Box,
  Typography,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MpesaIcon from "@mui/icons-material/PhoneAndroid";
import LinkIcon from "@mui/icons-material/Link";

import AppModal from "../common/AppModal";
import FormSelectField from "../common/FormSelectField";
import FormTextField from "../common/FormTextField";

import { recordInvoicePayment } from "../../redux/actions/invoiceActions";
import { getPaymentMethods } from "../../redux/actions/paymentMethodActions";

import StkPushModal from "../paymentIntegration/StkPushModal";
import AllocatePaymentModal from "../payments/AllocatePaymentModal";

const PayInvoiceModal = ({ open, onClose, invoice }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  // --- Redux State ---
  const { loadingPayInvoice } = useSelector((state) => state.invoice);
  const { paymentMethods } = useSelector((state) => state.paymentMethod);

  const customerPhone = invoice?.customerPhone || "";

  const balance = Number(invoice.total - invoice.amountPaid);

  // --- Sub-Modal State ---
  const [stkModalOpen, setStkModalOpen] = useState(false);
  const [allocateModalOpen, setAllocateModalOpen] = useState(false);

  useEffect(() => {
    if (!paymentMethods || paymentMethods.length === 0) {
      dispatch(getPaymentMethods());
    }
  }, [dispatch, paymentMethods]);

  const pmOptions = (paymentMethods || []).map((pm) => ({
    value: pm.id,
    label: pm.name,
  }));

  return (
    <AppModal
      open={open}
      onClose={loadingPayInvoice ? undefined : onClose}
      title={`Pay Invoice #${invoice.invoiceNumber}`}
      width="sm"
    >
      <Formik
        initialValues={{
          paymentMethodId: "",
          amount: balance,
          referenceCode: "",
          unallocatedPaymentId: null,
          unallocatedMaxAmount: null,
        }}
        validationSchema={Yup.object({
          paymentMethodId: Yup.string().required("Payment method required"),
          amount: Yup.number()
            .transform((value, originalValue) =>
              typeof originalValue === "string" ? Number(originalValue) : value,
            )
            .required("Amount is required")
            .positive("Must be greater than 0")
            .test("max-allowed", "Amount exceeds limits", function (value) {
              const { unallocatedMaxAmount } = this.parent;
              const maxAllowed = unallocatedMaxAmount
                ? Math.min(balance, unallocatedMaxAmount)
                : balance;

              if (value > maxAllowed + 0.01) {
                return this.createError({
                  message: `Cannot exceed ${maxAllowed.toFixed(2)}`,
                });
              }
              return true;
            }),
          referenceCode: Yup.string()
            .trim()
            .required("Reference code required"),
          unallocatedPaymentId: Yup.string().nullable(),
        })}
        validateOnMount={true}
        onSubmit={(values) => {
          const payload = {
            paymentMethodId: values.paymentMethodId,
            amount: Number(values.amount),
            referenceCode: values.referenceCode,
            unallocatedPaymentId: values.unallocatedPaymentId || null,
          };
          dispatch(recordInvoicePayment(invoice.id, payload));
        }}
      >
        {({ values, setValues, isValid }) => (
          <Form>
            <Stack spacing={2.5}>
              {/* BALANCE SUMMARY */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  borderRadius: 1,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Remaining Balance
                  </Typography>
                  <Typography
                    variant="h6"
                    color="secondary.main"
                    fontWeight="bold"
                  >
                    {balance.toFixed(2)}
                  </Typography>
                </Stack>
              </Box>

              {/* ELECTRONIC PAYMENTS INTEGRATION */}
              <Box
                sx={{
                  p: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={1}
                  fontWeight="bold"
                >
                  ELECTRONIC PAYMENTS
                </Typography>

                {values.unallocatedPaymentId ? (
                  <Box>
                    <Chip
                      color="success"
                      icon={<CheckCircleIcon />}
                      label={`Linked: ${values.referenceCode} (Available: ${values.unallocatedMaxAmount})`}
                      onDelete={() => {
                        setValues(
                          {
                            ...values,
                            unallocatedPaymentId: null,
                            referenceCode: "",
                            unallocatedMaxAmount: null,
                            amount: balance,
                          },
                          true,
                        );
                      }}
                      sx={{ fontWeight: "bold", mb: 1 }}
                    />
                    <Typography
                      variant="caption"
                      color="success.main"
                      display="block"
                    >
                      * You can edit the "Amount" field below to allocate a
                      partial amount.
                    </Typography>
                  </Box>
                ) : (
                  <Stack direction="row" spacing={2}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<MpesaIcon />}
                      onClick={() => setStkModalOpen(true)}
                    >
                      Send STK Push
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<LinkIcon />}
                      onClick={() => setAllocateModalOpen(true)}
                    >
                      Link Received Payment
                    </Button>
                  </Stack>
                )}
              </Box>

              {/* STANDARD FORM FIELDS */}
              <FormSelectField
                name="paymentMethodId"
                label="Payment Method"
                options={pmOptions}
              />
              <FormTextField
                name="amount"
                label="Amount to Allocate"
                type="number"
              />

              <FormTextField
                name="referenceCode"
                label="Reference Code / Transaction ID"
                InputProps={{ readOnly: !!values.unallocatedPaymentId }}
                sx={{
                  "& .MuiInputBase-input.Mui-readOnly": {
                    backgroundColor: alpha(
                      theme.palette.action.disabledBackground,
                      0.1,
                    ),
                    cursor: "not-allowed",
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={!isValid || loadingPayInvoice}
                sx={{ mt: 2, py: 1.5 }}
              >
                {loadingPayInvoice ? "Processing..." : "Record Payment"}
              </Button>
            </Stack>

            {/* --- SUB-MODALS --- */}
            {stkModalOpen && (
              <StkPushModal
                open={stkModalOpen}
                onClose={() => setStkModalOpen(false)}
                invoiceId={invoice.id}
                totalAmount={invoice.total}
                amountPaid={invoice.amountPaid}
                defaultPhone={customerPhone}
              />
            )}

            {allocateModalOpen && (
              <AllocatePaymentModal
                open={allocateModalOpen}
                onClose={() => setAllocateModalOpen(false)}
                onSelect={(payment) => {
                  const paymentAmount = Number(payment.amount);
                  const allocatableAmount = Math.min(paymentAmount, balance);

                  const mpesaMethod = pmOptions.find((o) =>
                    o.label.toLowerCase().includes("mpesa"),
                  );

                  setValues(
                    {
                      ...values,
                      unallocatedPaymentId: payment.id,
                      referenceCode: payment.gatewayReceiptNumber,
                      unallocatedMaxAmount: paymentAmount,
                      amount: Number(allocatableAmount.toFixed(2)),
                      paymentMethodId: mpesaMethod
                        ? mpesaMethod.value
                        : values.paymentMethodId,
                    },
                    true,
                  );

                  setAllocateModalOpen(false);
                }}
              />
            )}
          </Form>
        )}
      </Formik>
    </AppModal>
  );
};

export default PayInvoiceModal;
